import { createAsyncThunk } from '@reduxjs/toolkit';
import { GROUP_ADMIN } from '../../constant';
import { getToken } from '../../Utils/authUtils';

/**
 * Group Admin reads (/v2/group-admin/*) with the normal session token.
 * Grouped in one module the way rtk-apis/dashboard/billing is; pages dispatch and
 * unwrap, keeping the results in local state.
 */

export type StudyType = 'echo' | 'stressEcho';
/** The dashboard's open work: not finalized; unassigned; open over the overdue limit. */
export type Attention = 'open' | 'unassigned' | 'overdue';
export type ReportStatus = 'Draft' | 'Preliminary' | 'Finalized';

export interface GroupAdminClinic {
  _id: string;
  name: string | null;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface GroupAdminReport {
  _id: string;
  reportNumber: string | null;
  patientName: string | null;
  patientId: string | null;
  dob: string | null;
  sex: string | null;
  age: string | number | null;
  reportName: string | null;
  reportType: string | null;
  studyType: StudyType;
  status: ReportStatus | null;
  studyDate: string | null;
  studyTime: string | null;
  modality: string | null;
  studyInstanceUID: string | null;
  accessionNumber: string | null;
  hospitalId: string;
  clinicName: string | null;
  reportAssignedTo: string | null;
  sonographer: string | null;
  finalizedBy: string | null;
  finalizedAt: string | null;
  priority: string | null;
  createdOn: string | null;
  updatedAt: string | null;
}

export interface GroupAdminReportDetail extends GroupAdminReport {
  templateName: string | null;
  numberOfImages: number;
  readOnly: true;
}

export interface GroupAdminStudy {
  studyInstanceUID: string;
  patientName: string | null;
  patientId: string | null;
  dob: string | null;
  sex: string | null;
  studyDate: string | null;
  studyTime: string | null;
  accessionNumber: string | null;
  modalities: string[];
  description: string | null;
  referringPhysician: string | null;
  numberOfSeries: number;
  numberOfInstances: number;
  aeTitles: string[];
  clinics: { _id: string; name: string | null }[];
  reports: {
    _id: string;
    status: ReportStatus | null;
    reportType: string | null;
    clinicName: string | null;
    reportAssignedTo: string | null;
    createdOn: string | null;
  }[];
}

export interface LiteInstance {
  stage?: string;
  view?: string;
  instanceUID?: string;
  mediaUrl?: string;
  instanceNumber?: string;
  seriesNumber?: string;
}

export interface Measurement {
  measurementId: string;
  parameterName: string;
  category: string;
  value: number;
  unit: string | null;
  method: string | null;
  planeView: string | null;
  normalRange: { low?: number; high?: number; indexed?: boolean } | null;
  isAbnormal: boolean;
}

export interface DistributionRow {
  _id: string;
  channel: string;
  format: string | null;
  status: string;
  deliveryStage: string | null;
  from: string | null;
  to: string | null;
  ackCode: string | null;
  error: string | null;
  autoCreated: boolean;
  createdAt: string;
}

export interface ClinicRow {
  clinicId: string;
  name: string;
  location: string | null;
  reports: number;
  echo: number;
  stress: number;
  finalized: number;
  finalizedRate: number | null;
  studies: number;
  studiesApprox: boolean;
  reportedStudies: number;
  unreported: number;
  avgTatHours: number | null;
  statusMix: Record<string, number>;
  prevReports: number;
  reportsDelta: number | null;
  openReports: number;
  unassignedReports: number;
  overdueReports: number;
  distribution: DistributionSummary;
}

export interface DistributionSummary {
  total: number;
  delivered: number;
  failed: number;
  successRate: number | null;
}

export interface StaffRow {
  clinicId: string;
  clinic: string;
  name: string;
  role: 'Reporter' | 'Sonographer';
  reports: number;
  finalized: number | null;
  avgTatHours: number | null;
}

export interface FailedSend {
  _id: string;
  reportId: string;
  clinicId: string;
  clinic: string;
  patientName: string | null;
  channel: 'email' | 'medicalObject';
  to: string | null;
  status: string;
  error: string | null;
  createdAt: string;
}

export interface GroupAdminDashboard {
  period: { year: number; month: number | 'all'; label: string };
  totals: {
    clinics: number;
    activeClinics: number;
    reports: number;
    echo: number;
    stress: number;
    finalized: number;
    finalizedRate: number | null;
    studies: number;
    reportedStudies: number;
    unreported: number;
    avgTatHours: number | null;
    prevReports: number;
    reportsDelta: number | null;
    openReports: number;
    unassignedReports: number;
    overdueReports: number;
    overdueAfterHours: number;
  };
  statusTotals: Record<string, number>;
  clinics: ClinicRow[];
  weekdayVolume: { name: string; count: number }[];
  topReporters: { name: string; clinic: string; reports: number; finalized: number }[];
  topSonographers: { name: string; clinic: string; reports: number }[];
  staff: StaffRow[];
  distribution: DistributionSummary;
  failedSends: FailedSend[];
  trend: { categories: string[]; series: { name: string; data: number[] }[] };
  clinicTrend: { categories: string[]; series: { clinicId: string; name: string; data: number[] }[] };
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

const handle = async (res: Response, rejectWithValue: (v: unknown) => unknown) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    return rejectWithValue(body?.msg || 'Request failed');
  }
  return body;
};

const query = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
  });
  const qs = p.toString();
  return qs ? `?${qs}` : '';
};

const get = <Arg, Result>(type: string, url: (arg: Arg) => string) =>
  createAsyncThunk<Result, Arg>(`groupAdmin/${type}`, async (arg, { rejectWithValue }) => {
    try {
      const res = await fetch(url(arg), { headers: authHeaders() });
      const body: any = await handle(res, rejectWithValue);
      return body?.result ?? body;
    } catch (error) {
      return rejectWithValue(error) as any;
    }
  });

// ---- reads -------------------------------------------------------------------

export const getGroupAdminClinics = get<void, GroupAdminClinic[]>('clinics', () => GROUP_ADMIN.CLINICS);

export const getGroupAdminDashboard = get<
  { year?: number; month?: number | 'all'; from?: string; to?: string; clinicId?: string },
  GroupAdminDashboard
>('dashboard', (arg) => `${GROUP_ADMIN.DASHBOARD}${query(arg)}`);

export const getGroupAdminReports = get<
  {
    clinicId?: string;
    search?: string;
    status?: string;
    studyType?: StudyType | '';
    attention?: Attention | '';
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  },
  { data: GroupAdminReport[]; pagination: Pagination }
>('reports', (arg) => `${GROUP_ADMIN.REPORTS}${query(arg)}`);

export const getGroupAdminReport = get<string, GroupAdminReportDetail>('report', (id) => GROUP_ADMIN.REPORT(id));

export const getGroupAdminReportImages = get<string, { studyInstanceUID: string | null; instances: LiteInstance[] }>(
  'reportImages',
  (id) => GROUP_ADMIN.REPORT_LITE_IMAGES(id)
);

export const getGroupAdminReportMeasurements = get<string, { measurements: Measurement[] }>(
  'reportMeasurements',
  (id) => GROUP_ADMIN.REPORT_MEASUREMENTS(id)
);

export const getGroupAdminReportDistributions = get<string, { distributions: DistributionRow[] }>(
  'reportDistributions',
  (id) => GROUP_ADMIN.REPORT_DISTRIBUTIONS(id)
);

export const getGroupAdminStudies = get<
  {
    clinicId?: string;
    patientName?: string;
    patientId?: string;
    modality?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  },
  { data: GroupAdminStudy[]; pagination: Pagination }
>('studies', (arg) => `${GROUP_ADMIN.STUDIES}${query(arg)}`);

export const getGroupAdminStudyImages = get<string, { studyInstanceUID: string; instances: LiteInstance[] }>(
  'studyImages',
  (uid) => GROUP_ADMIN.STUDY_LITE_IMAGES(uid)
);

/** The report PDF as an object URL; the caller revokes it. */
export const getGroupAdminReportPdfUrl = createAsyncThunk(
  'groupAdmin/reportPdf',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await fetch(GROUP_ADMIN.REPORT_PDF(id), { headers: authHeaders() });
      if (!res.ok) {
        const body: any = await handle(res, rejectWithValue);
        return body;
      }
      return URL.createObjectURL(await res.blob());
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
